import { useState } from 'react'
import { router, usePage } from '@inertiajs/react'

import { Accordion } from '@components/atoms/Accordion'
import { RadioGroupField } from '@components/molecules/RadioGroupField'
import { PageLayout } from '@components/layouts/PageLayout'
import { scopedTranslation } from '@utils/I18n'

import { EmbedCodeBlock } from '@components/embeds/EmbedCodeBlock'
import { EmbedWorkspace, SettingsAccordionSection } from '@components/embeds/EmbedWorkspace'
import { PreviewShell } from '@components/embeds/PreviewShell'
import { embedCode } from '@utils/embeds/embedCode'

const t = scopedTranslation('embeds.groups_map')

export type GroupsMapPageProps = {
  embedHost: string
  mapPath: string
}

// The map sends no resize messages, so its box keeps one height.
const MAP_HEIGHT = 600

const METHODS = ['iframe', 'address'] as const

type Method = (typeof METHODS)[number]

export default function GroupsMapPage({ embedHost, mapPath }: GroupsMapPageProps) {
  const [path, search = ''] = usePage().url.split('?')
  const [method, setMethod] = useState<Method>(() =>
    new URLSearchParams(search).get('method') === 'address' ? 'address' : 'iframe'
  )
  const address = `${embedHost}${mapPath}`
  const output = method === 'address' ? 'address' : 'code'
  const text =
    method === 'address'
      ? address
      : embedCode({ address, resizeScriptUrl: '', title: t('code.title'), height: MAP_HEIGHT })

  const changeMethod = (next: Method) => {
    setMethod(next)
    router.replace({
      url: next === 'address' ? `${path}?method=address` : path,
      preserveState: true,
      preserveScroll: true,
    })
  }

  return (
    <PageLayout.Root>
      <PageLayout.Header heading={t('heading')} icon="code-browser" subtitle={t('subtitle')} />
      <PageLayout.Body>
        <EmbedWorkspace
          settings={
            <Accordion type="multiple" defaultValue={['installation', 'code']}>
              <SettingsAccordionSection value="installation" title={t('sections.installation')}>
                <RadioGroupField<Method>
                  label={t('method.label')}
                  value={method}
                  onValueChange={changeMethod}
                  options={METHODS.map((option) => ({
                    value: option,
                    label: t(`method.${option}`),
                    description: t(`method.${option}_hint`),
                  }))}
                />
              </SettingsAccordionSection>
              <SettingsAccordionSection value="code" title={t('sections.code')}>
                <p className="tw:m-0 tw:text-sm tw:text-gray-cool-700">{t(`${output}.paste`)}</p>
                <EmbedCodeBlock
                  label={t(`${output}.label`)}
                  code={text}
                  copyLabel={t(`${output}.copy`)}
                  copiedMessage={t(`${output}.copied`)}
                />
              </SettingsAccordionSection>
            </Accordion>
          }
          preview={
            <PreviewShell host={t('preview_host')} note={t('preview_note')}>
              <iframe
                src={mapPath}
                title={t('preview_title')}
                className="tw:block tw:w-full tw:border-0 tw:bg-white"
                style={{ height: MAP_HEIGHT }}
              />
            </PreviewShell>
          }
        />
      </PageLayout.Body>
    </PageLayout.Root>
  )
}
